import {
  Input,
  Pagination,
  SortDescriptor,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from '@nextui-org/react'
import React from 'react'

export type Column = {
  name: string
  uid: string
  sortable?: boolean
}

interface DataTableProps<T> {
  items: T[]
  columns: Column[]
  page: number
  pages: number
  rowsPerPage: number
  filterValue: string
  sortDescriptor: SortDescriptor
  isLoading?: boolean
  onSearchChange: (value: string) => void
  onClear: () => void
  onRowsPerPageChange: (value: number) => void
  onPageChange: (page: number) => void
  onSortChange: (descriptor: SortDescriptor) => void
  renderCell: (item: T, columnKey: string) => React.ReactNode
  searchPlaceholder?: string
  totalCount?: number
}

const SearchIcon = () => (
  <svg
    aria-hidden='true'
    fill='none'
    height='1em'
    role='presentation'
    viewBox='0 0 24 24'
    width='1em'
    stroke='currentColor'
  >
    <path
      d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='2'
    />
  </svg>
)

export function DataTable<T extends { id: string | number }>({
  items,
  columns,
  page,
  pages,
  rowsPerPage,
  filterValue,
  sortDescriptor,
  isLoading = false,
  onSearchChange,
  onClear,
  onRowsPerPageChange,
  onPageChange,
  onSortChange,
  renderCell,
  searchPlaceholder = 'Search...',
  totalCount = 0
}: DataTableProps<T>) {
  const topContent = React.useMemo(() => {
    return (
      <div className='flex flex-col gap-4'>
        <div className='flex justify-between gap-3 items-end'>
          <Input
            isClearable
            className='w-full sm:max-w-[44%]'
            placeholder={searchPlaceholder}
            startContent={<SearchIcon />}
            value={filterValue}
            onClear={onClear}
            onValueChange={onSearchChange}
          />
        </div>
        <div className='flex justify-between items-center'>
          <span className='text-default-400 text-small'>Total {totalCount} items</span>
          <label className='flex items-center text-default-400 text-small'>
            Rows per page:
            <select
              className='bg-transparent outline-none text-default-400 text-small'
              onChange={e => onRowsPerPageChange(Number(e.target.value))}
              value={rowsPerPage}
            >
              <option value='20'>20</option>
              <option value='50'>50</option>
              <option value='100'>100</option>
            </select>
          </label>
        </div>
      </div>
    )
  }, [
    filterValue,
    totalCount,
    onClear,
    onRowsPerPageChange,
    rowsPerPage,
    searchPlaceholder,
    onSearchChange
  ])

  const bottomContent = React.useMemo(() => {
    // Ensure page is within valid range for Pagination component
    // This prevents issues when page from URL exceeds current pages (e.g., during loading)
    const validPage = Math.min(Math.max(1, page), Math.max(1, pages))
    return (
      <div className='py-2 px-2 flex justify-between items-center'>
        <Pagination
          showControls
          showShadow
          color='primary'
          page={validPage}
          total={pages}
          onChange={onPageChange}
        />
      </div>
    )
  }, [page, pages, onPageChange])

  return (
    <Table
      aria-label='Data table'
      isHeaderSticky
      bottomContent={bottomContent}
      bottomContentPlacement='outside'
      classNames={{
        wrapper: 'max-h-[calc(100vh-400px)]'
      }}
      sortDescriptor={sortDescriptor}
      topContent={topContent}
      topContentPlacement='outside'
      onSortChange={onSortChange}
    >
      <TableHeader columns={columns}>
        {column => (
          <TableColumn
            key={column.uid}
            align={column.uid === 'actions' ? 'center' : 'start'}
            allowsSorting={column.sortable}
          >
            {column.name}
          </TableColumn>
        )}
      </TableHeader>
      <TableBody
        items={items}
        loadingContent={<div>Loading...</div>}
        loadingState={isLoading ? 'loading' : 'idle'}
      >
        {item => (
          <TableRow key={item.id}>
            {columnKey => <TableCell>{renderCell(item, columnKey as string)}</TableCell>}
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}
